import React from 'react';
import { HelpH, HelpList, HelpP, HelpPath } from '@/components/common/PageHelp';
import { AiQuotaExample } from '@/components/common/AiReportNotice';

/**
 * 화면 설명 (사용자 가이드)
 * ---------------------------------------------------------------------------
 * 화면마다 "여기가 무엇이고, 여기서 한 일이 어디에 영향을 주는지" 를 적는다.
 *
 * ## 왜 한곳에 모았나
 * 화면이 83개다. 파일마다 설명을 심으면 같은 말이 흩어지고 어떤 화면에는
 * 있고 어떤 화면에는 없게 된다. 여기 한 표에 모으면 빠진 화면이 바로 보인다.
 * 레이아웃이 현재 주소로 이 표를 찾아 경로 표시줄 오른쪽에 같은 모양으로 띄운다.
 *
 * ## 무엇을 쓰나
 * "이 버튼은 저장합니다" 같은 말은 쓰지 않는다 — 보면 안다.
 * **화면만 봐서는 알 수 없는 것**을 쓴다.
 *   · 여기서 넣은 값이 어느 화면에 나타나는지
 *   · 순서를 틀리면 무엇이 막히는지
 *   · 지우면 되돌릴 수 있는지
 *   · WORK&Hub 와 어떻게 이어지는지
 *
 * ## 키
 * 메뉴의 주소(`tbl_menus.url_path`)를 그대로 쓴다. 메뉴를 추가하면 여기에도
 * 한 줄 더한다.
 */
export const SCREEN_GUIDE = {
  /* ===================== 대시보드 ===================== */
  '/dashboard': {
    title: '대시보드 — 화면 설명',
    body: (
      <>
        <HelpP>
          오늘 내가 할 일을 한 화면에 모았습니다. 결재할 문서, 오늘 일정, 내가 맡은 업무,
          최근 알림이 보입니다.
        </HelpP>
        <HelpP>
          각 카드의 제목을 누르면 그 화면으로 넘어갑니다. 보이는 내용은 <b>내 권한 안의
          것만</b>입니다 — 같은 화면이라도 사람마다 다르게 보입니다.
        </HelpP>
      </>
    ),
  },

  /* ===================== 고객관리 ===================== */
  '/customers/list': {
    title: '고객사관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          거래처를 등록하고 관리합니다. <b>여기에 등록된 고객사가 다른 화면의 「고객사」
          선택 목록에 나타납니다</b> — 프로젝트, 영업기회, 견적 모두 여기서 고릅니다.
        </HelpP>
        <HelpH>처음이라면 이 순서로</HelpH>
        <HelpList
          items={[
            { term: '1. 고객사 등록', desc: '상호·사업자번호·주소를 넣습니다.' },
            {
              term: '2. 고객직원 등록',
              desc: (
                <>
                  담당자를 <HelpPath>고객직원관리</HelpPath> 에 넣습니다. 영업활동과 프로젝트에서
                  "누구와 이야기했는지" 를 남길 수 있게 됩니다.
                </>
              ),
            },
            {
              term: '3. 영업기회 등록',
              desc: '거래 가능성이 보이면 잠재고객 및 영업기회에 올립니다.',
            },
          ]}
        />
        <HelpH>거래중 여부 — 지우기 전에 먼저 이것부터</HelpH>
        <HelpP>
          거래를 끝낸 곳은 지우지 말고 <b>거래중을 꺼 두세요.</b> 꺼 두면 프로젝트·업무 화면의
          고객사 고르기 목록에서만 빠지고, 이 화면과 <HelpPath>고객현황</HelpPath> 에는 그대로
          남습니다. 과거 기록도 전부 보존됩니다.
        </HelpP>
        <HelpH>지우면 어떻게 되나</HelpH>
        <HelpList
          items={[
            {
              term: '프로젝트가 있으면 아예 안 지워집니다',
              desc: '그 고객사로 만든 프로젝트가 한 건이라도 있으면 삭제가 거절됩니다. 프로젝트를 먼저 정리하거나, 거래중을 꺼 두세요.',
            },
            {
              term: '담당자는 함께 사라집니다',
              desc: (
                <>
                  <HelpPath>고객직원관리</HelpPath> 에 넣어 둔 그 고객사의 담당자가{' '}
                  <b>같이 지워집니다.</b> 되돌릴 수 없습니다.
                </>
              ),
            },
          ]}
        />
        <HelpH>엑셀</HelpH>
        <HelpP>
          <b>양식 내려받기</b> 로 빈 서식을 받아 채운 뒤 <b>일괄 등록</b> 하면 여러 건을 한
          번에 넣을 수 있습니다. 양식의 열 이름은 바꾸지 마세요 — 그 이름으로 칸을 찾습니다.
        </HelpP>
      </>
    ),
  },

  '/customers/detail': {
    title: '고객사 상세 — 화면 설명',
    body: (
      <>
        <HelpP>
          고객사 한 곳의 정보를 모아 봅니다. <b>수정</b> 을 누르면 이 화면에서 바로 고칠 수
          있고, 아래쪽에는 그 고객사의 <HelpPath>소속 직원 목록</HelpPath> 과{' '}
          <b>첨부된 고객 서류</b> 가 함께 붙습니다.
        </HelpP>

        <HelpH>위치 정보 — 주소와 지도</HelpH>
        <HelpP>
          주소를 넣어 두면 그 아래에 <b>카카오 지도</b> 가 뜨고, 핀으로 위치를 찍어 줍니다.
          지도 밑의 <b>카카오맵 · 네이버지도 · 구글지도</b> 단추는 길찾기용입니다 — 평소
          쓰시던 앱에서 바로 열립니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '지도가 안 뜨고 단추만 나올 때도 있습니다',
              desc: '주소를 지도에서 찾지 못했거나, 지도 서비스에 일시적으로 닿지 못한 경우입니다. 이때도 단추로 열면 보입니다 — 화면이 고장난 것이 아닙니다.',
            },
            {
              term: '지도에는 상세 주소를 넣지 않습니다',
              desc: '층·동·호수가 붙으면 지도가 오히려 건물을 못 찾습니다. 도로명주소까지만 넘깁니다. 주소가 잘 안 찾아지면 수정에서 건물명을 함께 적어 보세요.',
            },
            {
              term: '주소가 비어 있으면 지도 자리도 나오지 않습니다',
              desc: '수정에서 주소를 넣으면 바로 나타납니다.',
            },
          ]}
        />

        <HelpH>여기서 고친 내용이 어디까지 퍼지나</HelpH>
        <HelpP>
          상호·사업자번호 같은 기본 정보는 <HelpPath>고객사관리</HelpPath> 목록과 같은
          자료입니다. 여기서 고치면 프로젝트·영업기회·견적 화면의 고객사 표시도 함께
          바뀝니다.
        </HelpP>
      </>
    ),
  },

  '/customers/employees': {
    title: '고객직원관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          고객사에서 우리와 일하는 <b>사람</b>을 관리합니다. 고객사는 회사, 여기는 그 회사의
          담당자입니다.
        </HelpP>
        <HelpP>
          먼저 <HelpPath>고객사관리</HelpPath> 에 회사가 있어야 그 아래에 사람을 넣을 수
          있습니다. 담당자가 바뀌면 지우지 말고 새 사람을 넣으세요 — 과거 영업활동이 누구와
          한 것인지 남아 있어야 합니다.
        </HelpP>
        <HelpP>
          고객사를 고르는 칸에는 <b>거래중인 곳만</b> 나옵니다. 다만 지금 보고 있는 담당자가
          거래중지된 고객사 소속이면 그 회사도 함께 띄워 줍니다 — 그렇지 않으면 고칠 방법이
          없기 때문입니다.
        </HelpP>
        <HelpP>
          <b>고객사를 지우면 그 아래 담당자도 함께 지워집니다.</b> 여기서 따로 지우지
          않아도 사라지니, 고객사를 정리하기 전에 확인해 주세요.
        </HelpP>
        <HelpP>
          메모 칸은 우리끼리 보는 자리입니다. 프로젝트·업무 화면에서 담당자를 고를 때는
          메모를 빼고 이름·직급·연락처만 보여 줍니다.
        </HelpP>
      </>
    ),
  },

  '/crm/opportunities': {
    title: '잠재고객 및 영업기회 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>아직 계약이 아닌 건</b>을 관리합니다. 관심을 보인 곳, 견적을 낸 곳, 경쟁 중인
          곳이 여기에 모입니다.
        </HelpP>
        <HelpH>영업활동은 여기에 매답니다</HelpH>
        <HelpP>
          방문·통화 같은 기록은 <b>영업기회 하나에 매달려</b> 쌓입니다. 그래서 영업기회를
          먼저 만들어야 <HelpPath>영업활동 관리</HelpPath> 에서 활동을 적을 수 있습니다.
        </HelpP>
        <HelpH>예상 금액과 확률</HelpH>
        <HelpP>
          둘을 곱한 값이 영업 보고서의 <b>가중 매출 전망</b>이 됩니다. 확률을 적어 두면
          "얼마나 들어올 것 같은가" 를 자동으로 계산해 줍니다. 확률은 0~100 사이,
          예상 금액은 0 이상만 받습니다.
        </HelpP>
        <HelpH>지우면 활동과 보고서가 함께 사라집니다</HelpH>
        <HelpP>
          영업기회를 지우면 거기에 매달린 <b>영업활동 기록과 AI 보고서가 모두 같이
          지워집니다.</b> 되돌릴 수 없습니다. 끝난 건은 지우는 대신 진행 단계를 「계약」이나
          「실주 및 기타」로 바꿔 두세요 — 그래야 나중에 왜 그렇게 됐는지 되짚을 수 있습니다.
        </HelpP>
        <HelpP>
          다만 「실주 및 기타」로 바꾸면 <HelpPath>영업활동 보고서</HelpPath> 의 분석
          대상에서 빠집니다. 보고서를 먼저 남겨 두고 단계를 바꾸세요.
        </HelpP>
      </>
    ),
  },

  '/sales/activities': {
    title: '영업활동 관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          방문·통화·메일 같은 <b>접촉 기록</b>을 남깁니다. 나중에 "이 건을 누가 언제 어떻게
          끌고 왔는지" 를 되짚는 자리입니다.
        </HelpP>
        <HelpP>
          활동은 <HelpPath>잠재고객 및 영업기회</HelpPath> 에 매달립니다. 기록할 영업기회가
          없으면 먼저 그쪽에 만들어 주세요.
        </HelpP>
        <HelpH>작성자는 자동으로 남습니다</HelpH>
        <HelpP>
          활동을 적으면 <b>로그인한 사람이 작성자로 기록됩니다</b> — 따로 고를 수 없고 나중에
          바꿀 수도 없습니다. 그래서 <b>고치거나 지우는 것은 본인(과 시스템 관리자)만</b>
          됩니다. 남이 적은 활동에는 수정·삭제 단추가 보이지 않습니다.
        </HelpP>
        <HelpP>
          누가 적을 수 있는지는 <HelpPath>설정 → 권한관리</HelpPath> 의{' '}
          <b>「잠재고객 및 영업기회」</b> 메뉴를 따릅니다 — 활동 타임라인은 그 화면 안에
          있으므로 별도 메뉴가 아닙니다. 등록·수정·삭제 칸을 끄면 입력칸과 단추가 사라지고
          서버에서도 막힙니다. 「영업활동 관리」 메뉴는 고객 거래를 다루는 다른 화면입니다.
        </HelpP>
        <HelpP>
          쌓인 활동은 <HelpPath>영업활동 보고서</HelpPath> 가 읽어 갑니다. 활동을 지우면 그
          내용은 이후 보고서에서 빠집니다.
        </HelpP>
      </>
    ),
  },

  '/sales/reports': {
    title: '영업활동 보고서 — 화면 설명',
    body: (
      <>
        <HelpP>
          영업기회 하나를 골라 <b>그 기간의 활동을 AI 가 읽고</b> 현황 요약·리스크·다음
          할 일로 정리해 줍니다. 직접 쓰지 않아도 쌓인 활동 기록에서 만들어집니다.
        </HelpP>
        <HelpH>기간을 고릅니다 — 최대 1개월</HelpH>
        <HelpP>
          고른 기간에 기록된 영업활동만 분석합니다(기본값 최근 1개월). 예전에는 그
          영업기회의 활동을 <b>처음부터 끝까지</b> 다 보냈는데, 한 해 넘게 굴러온 건은
          요약이 「꾸준히 접촉하고 있습니다」로 뭉개졌습니다. 자료가 많을수록 분석이
          흐려지고 비용도 늘어나므로 한 번에 1개월까지만 봅니다.
        </HelpP>
        <HelpH>매월 무료 수량이 있고, 넘긴 만큼만 청구됩니다</HelpH>
        <HelpP>
          화면 위에 <AiQuotaExample /> 가 떠서 이번 달에 몇 건을 만들었는지 보여 줍니다.
          괄호 안의 무료 수량은 지금 <b>계약하신 요금제에서 읽어 온 값</b>이라 기업마다
          다릅니다. 무료 수량은{' '}
          <b>기업 단위</b>(사람 수와 무관)이고{' '}
          <b>매월 1일에 새로 시작</b>합니다. 쓰지 않은 수량은 <b>다음 달로 넘어가지
          않습니다.</b> 무료 수량을 넘기면 <b>건당 비용이 발생</b>하고, 단가는 계약하신
          요금제의 금액입니다 — 이 숫자들은 모두 WORK&amp;Hub 가 세는 값을 그대로 받아
          옵니다. 청구서와 어긋날 일이 없습니다.
        </HelpP>
        <HelpP>
          <b>만들다 실패하면 청구하지 않습니다.</b> 보고서는{' '}
          <b>WORK&amp;Hub 에서 제공하는 AI 서비스</b>로 만듭니다 — 어느 모델을 쓸지는
          공급사가 정하므로 기업에서 고르실 것은 없습니다.
        </HelpP>
        <HelpH>만들기 전에 동의가 필요합니다</HelpH>
        <HelpP>
          보고서를 만들면 고객사 정보와 영업활동 내용이 <b>선택된 AI 모델로 전달</b>됩니다.
          그래서 만들 때마다 동의 체크를 받고, <b>동의한 사실과 그때의 문구를 보고서마다
          기록</b>합니다. 체크하지 않으면 [분석 시작] 이 눌리지 않고, 서버도 거절합니다.
        </HelpP>
        <HelpP>
          내용이 비어 보인다면 그 기간에 활동 기록이 없는 것입니다. 먼저{' '}
          <HelpPath>영업활동 관리</HelpPath> 에 접촉 내용을 남기거나 기간을 넓혀 주세요.
        </HelpP>
        <HelpP>
          고를 수 있는 영업기회에 <b>「실주 및 기타」 단계는 나오지 않습니다</b> — 끝난 건은
          분석 대상이 아닙니다. 보고서가 필요하면 단계를 바꾸기 전에 만들어 두세요.
        </HelpP>
      </>
    ),
  },

  '/customers/status': {
    title: '고객현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          고객사별로 <b>어떤 프로젝트가 돌아가고 있는지</b>를 모아 봅니다. 왼쪽에서 고객사를
          고르면 오른쪽에 그 고객사의 프로젝트 목록이 뜨고, 프로젝트를 누르면 업무·참여자·
          첨부까지 펼쳐 볼 수 있습니다.
        </HelpP>
        <HelpP>
          목록에는 <b>프로젝트 건수와 매출 합계</b>가 함께 나옵니다 — 고르기 전에도 거래
          규모를 가늠할 수 있습니다. 매출은 각 프로젝트의 예상 매출을 더한 값입니다.
        </HelpP>
        <HelpP>
          <b>거래중지한 고객사도 여기에는 남습니다.</b> 위쪽 「거래상태」로 걸러 보세요.
          영업기회·영업활동은 이 화면에 나오지 않습니다 —{' '}
          <HelpPath>잠재고객 및 영업기회</HelpPath> 에서 보세요.
        </HelpP>
        <HelpP>읽기 전용입니다. 고치려면 각 화면으로 가세요.</HelpP>
      </>
    ),
  },

  /* ===================== 프로젝트 ===================== */
  '/projects/list': {
    title: '프로젝트 — 화면 설명',
    body: (
      <>
        <HelpP>
          수주한 일을 프로젝트로 등록합니다. <b>프로젝트가 있어야 업무·일정·첨부·비용이
          매달립니다.</b>
        </HelpP>
        <HelpH>프로젝트 코드</HelpH>
        <HelpP>
          프로젝트를 가리키는 이름표입니다. <b>자동 생성</b>을 쓰면 겹치지 않는 코드를 만들어
          줍니다. 직접 정할 수도 있지만 한 번 정하면 바꾸기 어렵습니다 — 업무·첨부가 이
          코드로 붙어 있습니다.
        </HelpP>
        <HelpH>고객사는 미리 등록해 두세요</HelpH>
        <HelpP>
          프로젝트는 고객사를 골라야 저장됩니다. 없으면{' '}
          <HelpPath>고객관리 → 고객사관리</HelpPath> 에 먼저 넣어 주세요.
        </HelpP>
      </>
    ),
  },

  '/projects/tasks': {
    title: '업무관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          프로젝트 안의 <b>할 일</b>을 등록하고 진행 상태를 관리합니다. 담당자와 기간을
          넣으면 아래 화면들이 자동으로 채워집니다.
        </HelpP>
        <HelpList
          items={[
            { term: '업무일정', desc: '달력에 기간이 그려집니다.' },
            { term: '진척현황(로드맵)', desc: '프로젝트별 진행률이 막대로 보입니다.' },
            { term: '업무참여현황', desc: '사람별로 지금 몇 건을 맡고 있는지 집계됩니다.' },
          ]}
        />
        <HelpP>
          기간을 비워 두면 달력과 로드맵에 나타나지 않습니다. 날짜를 모르면 대략이라도
          넣어 두는 편이 낫습니다.
        </HelpP>
      </>
    ),
  },

  '/projects/calendar': {
    title: '업무일정 — 화면 설명',
    body: (
      <>
        <HelpP>
          등록된 업무를 달력으로 봅니다. 여기서 직접 만들지 않고{' '}
          <HelpPath>업무관리</HelpPath> 에 넣은 것이 그려집니다.
        </HelpP>
        <HelpP>달력에 없는 업무는 시작일·종료일이 비어 있는 것입니다.</HelpP>
      </>
    ),
  },

  '/projects/roadmap': {
    title: '진척현황(로드맵) — 화면 설명',
    body: (
      <>
        <HelpP>
          프로젝트가 <b>계획대로 가고 있는지</b>를 한눈에 봅니다. 업무의 상태와 기간으로
          진행률을 계산합니다.
        </HelpP>
        <HelpP>
          숫자가 이상하면 업무의 상태가 실제와 다른 경우가 많습니다 — 끝난 업무를 「진행」
          으로 두면 영영 100%가 되지 않습니다.
        </HelpP>
      </>
    ),
  },

  '/projects/gantt': {
    title: '칸트차트 — 화면 설명',
    body: (
      <>
        <HelpP>
          업무를 시간 축에 막대로 늘어놓아 <b>무엇이 겹치고 무엇이 비는지</b>를 봅니다.
          로드맵이 진행률이라면 여기는 일정입니다.
        </HelpP>
      </>
    ),
  },

  '/projects/filepages': {
    title: '통합첨부파일 — 화면 설명',
    body: (
      <>
        <HelpP>
          프로젝트와 업무에 올린 파일을 <b>한 자리에 모아</b> 봅니다. 어느 화면에서 올렸든
          여기서 찾을 수 있습니다.
        </HelpP>
        <HelpP>
          파일을 지우는 것은 올린 본인과 관리자만 할 수 있습니다. 다른 사람의 첨부는 보이되
          지워지지 않습니다.
        </HelpP>
      </>
    ),
  },

  '/projects/cost': {
    title: '프로젝트 비용현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          프로젝트에 들어간 돈을 봅니다. 전자결재로 <b>승인이 끝난</b> 지출 문서가 집계
          대상입니다.
        </HelpP>
        <HelpP>
          숫자가 비어 있다면 지출 문서에 프로젝트를 연결하지 않은 것입니다. 기안할 때
          프로젝트를 골라 주세요.
        </HelpP>
      </>
    ),
  },

  '/project/reports': {
    title: '프로젝트 보고서 — 화면 설명',
    body: (
      <>
        <HelpP>
          프로젝트 여러 건의 진행 상황을 <b>AI 가 한 장으로</b> 묶습니다. 업무 기록에서
          끌어오므로 따로 쓰지 않아도 초안이 만들어집니다.
        </HelpP>
        <HelpH>목록에서 찾기 — 기본은 최근 두 달</HelpH>
        <HelpP>
          생성일시는 <b>두 달 전부터 오늘까지</b>로 열립니다. 그보다 예전 보고서는 목록에
          보이지 않으니, 안 보인다고 지워진 것이 아닙니다 — <b>시작일을 앞으로 당기면</b>{' '}
          나옵니다. <b>보고서 제목</b> 칸에는 제목의 일부만 쳐도 되고, 영문 대소문자는
          가리지 않습니다.
        </HelpP>
        <HelpP>
          목록에는 <b>만들 때 붙인 제목</b>이 먼저 보이고, 그 아래에 어느 프로젝트를 묶은
          것인지가 작게 붙습니다. 제목은 만들 때 반드시 적어야 하므로 나중에 이 제목으로
          찾게 됩니다 — <b>「10월 2주 통합」</b> 처럼 기간이 드러나게 적어 두시면 찾기가
          쉽습니다.
        </HelpP>
        <HelpH>무엇을 보낼지 고릅니다</HelpH>
        <HelpP>
          프로젝트 내용·계획내용·업무내용·코멘트 중 <b>고른 것만</b> AI 에 보냅니다. 고르지
          않은 자료는 나가지 않습니다. 조건을 바꾸면 몇 건이 모이는지 아래에 미리
          보여 주므로, 만들기 전에 분석할 거리가 있는지 확인할 수 있습니다.
        </HelpP>
        <HelpH>기간은 최대 1개월</HelpH>
        <HelpP>
          주간·월간 단추로 기간을 맞추고 직접 고쳐도 됩니다. 한 번에 <b>1개월까지</b>만
          분석합니다 — 자료가 많을수록 요약이 뭉개지고 비용도 늘어납니다.
        </HelpP>
        <HelpH>매월 무료 수량이 있고, 넘긴 만큼만 청구됩니다</HelpH>
        <HelpP>
          만들기 단추 위에 <AiQuotaExample /> 가 떠서 이번 달에 몇 건을 만들었는지 보여
          줍니다. 괄호 안의 무료 수량은 지금 <b>계약하신 요금제에서 읽어 온 값</b>이라
          기업마다 다릅니다. 무료 수량은
          <b>기업 단위</b>(사람 수와
          무관)이고 <b>매월 1일에 새로 시작</b>하며, 쓰지 않은 수량은 <b>다음 달로
          넘어가지 않습니다.</b> 무료 수량을 넘기면 <b>건당 비용이 발생</b>하고, 단가는
          계약하신 요금제의 금액입니다 — 이 숫자는 모두 WORK&amp;Hub 가 세는 값을 그대로
          받아 오므로 청구서와 어긋나지 않습니다.
        </HelpP>
        <HelpP>
          <b>만들다 실패하면 청구하지 않습니다.</b> 보고서는{' '}
          <b>WORK&amp;Hub 에서 제공하는 AI 서비스</b>로 만듭니다 — 어느 모델을 쓸지는
          공급사가 정하므로 기업에서 고르실 것은 없습니다.
        </HelpP>
        <HelpH>만들기 전에 동의가 필요합니다</HelpH>
        <HelpP>
          보고서를 만들면 고른 프로젝트·업무 내용이 <b>선택된 AI 모델로 전달</b>됩니다.
          그래서 만들 때마다 동의 체크를 받고, <b>동의한 사실과 그때의 문구를 보고서마다
          기록</b>합니다. 체크하지 않으면 [분석 시작] 이 눌리지 않고, 서버도 거절합니다.
        </HelpP>
      </>
    ),
  },

  /* ===================== 재고관리 ===================== */
  '/inventory/items': {
    title: '품목마스터 — 화면 설명',
    body: (
      <>
        <HelpP>
          취급하는 물건의 <b>목록</b>입니다. 여기에 없는 품목은 입출고도 실사도 할 수
          없습니다 — 재고관리는 이 화면에서 시작합니다.
        </HelpP>
        <HelpH>시리얼번호가 없는 품목이라면</HelpH>
        <HelpP>
          그래도 비워 둘 수 없습니다. 입출고와 실사가 이 값으로 물건을 가립니다. 제조사
          일련번호가 없으면 <b>사내 관리번호</b>를 정해 적어 주세요(예:{' '}
          <code>N/A-ITM-001</code>).
        </HelpP>
        <HelpH>현재 재고는 직접 고치지 않습니다</HelpH>
        <HelpP>
          재고 수량은 <HelpPath>입출고관리</HelpPath> 와 <HelpPath>재고실사</HelpPath> 를
          거쳐서만 바뀝니다. 그래야 "왜 줄었는지" 가 기록으로 남습니다.
        </HelpP>
        <HelpH>입출고 이력이 있으면 지울 수 없습니다</HelpH>
        <HelpP>
          한 번이라도 들어오고 나간 품목은 삭제가 막힙니다 — 지우면 그 입출고 기록이
          무엇이었는지 알 수 없게 되기 때문입니다. 더 취급하지 않을 품목이라면{' '}
          <b>사용여부</b>를 꺼 주세요. 목록과 드롭다운에서 빠지고 지난 기록은 그대로
          남습니다.
        </HelpP>
      </>
    ),
  },

  '/inventory/status': {
    title: '입출고관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          물건이 들어오고 나간 것을 기록합니다. <b>여기 적은 것이 그대로 재고 수량이
          됩니다.</b>
        </HelpP>
        <HelpList
          items={[
            { term: '입고', desc: '들어온 수량만큼 재고가 늘어납니다. 단가를 함께 넣으면 품목 단가도 갱신할 수 있습니다.' },
            { term: '출고', desc: '나간 수량만큼 줄어듭니다. 재고보다 많이 출고할 수는 없습니다.' },
          ]}
        />
        <HelpH>마감한 달은 고칠 수 없습니다</HelpH>
        <HelpP>
          <HelpPath>월별재고마감현황</HelpPath> 에서 마감한 달에는 입출고를 넣을 수
          없습니다. 마감값과 실제가 어긋나면 되돌릴 방법이 없기 때문입니다. 꼭 고쳐야 하면
          마감을 취소한 뒤에 하세요.
        </HelpP>
      </>
    ),
  },

  '/inventory/stock-status': {
    title: '재고현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          품목별로 지금 얼마가 남았는지 봅니다. 입출고와 실사 결과가 합쳐진 값입니다.
        </HelpP>
        <HelpP>
          숫자가 실제와 다르면 <HelpPath>재고실사</HelpPath> 로 맞추세요. 여기서 직접
          고치지 않습니다.
        </HelpP>
      </>
    ),
  },

  '/inventory/by-location': {
    title: '위치별재고 — 화면 설명',
    body: (
      <>
        <HelpP>
          같은 품목이 <b>어느 창고에 얼마나</b> 있는지 봅니다. 입출고를 등록할 때 넣은
          위치가 기준입니다.
        </HelpP>
        <HelpP>위치를 비워 두고 등록하면 여기서 묶이지 않습니다.</HelpP>
      </>
    ),
  },

  '/inventory/count': {
    title: '재고실사 — 화면 설명',
    body: (
      <>
        <HelpP>
          장부와 실제가 맞는지 세어 보고 <b>차이를 맞추는</b> 화면입니다.
        </HelpP>
        <HelpH>순서</HelpH>
        <HelpList
          items={[
            { term: '1. 실사 문서 만들기', desc: '날짜·창고를 정하고 셀 품목을 고릅니다.' },
            { term: '2. 실사 수량 입력', desc: '직접 센 수량을 적습니다. 장부 수량과 나란히 보입니다.' },
            {
              term: '3. 확정',
              desc: (
                <>
                  <b>확정을 눌러야 재고가 바뀝니다.</b> 차이만큼 조정 내역이 자동으로 생기고,
                  입출고 목록에 「재고실사」로 남습니다.
                </>
              ),
            },
          ]}
        />
        <HelpP>확정한 실사는 되돌릴 수 없습니다. 수량을 다시 확인하고 누르세요.</HelpP>
      </>
    ),
  },

  '/inventory/rentals': {
    title: '대여품반납관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          빌려준 물건이 <b>돌아왔는지</b>를 봅니다. 출고할 때 대여자와 반납 예정일을 적어
          두면 여기에 모입니다.
        </HelpP>
        <HelpP>반납 예정일이 지난 것이 위에 뜹니다.</HelpP>
      </>
    ),
  },

  '/inventory/monthly-closing': {
    title: '월별재고마감현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          한 달을 닫아 그 시점의 재고를 <b>고정</b>합니다. 마감한 달에는 입출고를 넣거나
          고칠 수 없습니다.
        </HelpP>
        <HelpP>
          잘못 마감했으면 취소할 수 있습니다. 다만 그 사이에 넣은 자료가 어긋날 수 있으니
          확인하고 다시 마감하세요.
        </HelpP>
        <HelpP>
          <b>지나간 달만</b> 마감할 수 있습니다 — 이번 달은 아직 입출고가 남아 있어
          고정할 수 없습니다. 마감·마감취소는 이 메뉴의 <b>등록 권한</b>이 있는 사람만
          할 수 있고, 조회 권한만 있으면 결과만 봅니다.
        </HelpP>
      </>
    ),
  },

  /* ===================== 전자결재 ===================== */
  /*
   * 결재함이 여럿인 이유는 **문서가 지금 어디 있는지**로 나눠 두기 위해서다.
   * 같은 문서가 결재가 진행되면서 함을 옮겨 다닌다. 그 흐름을 적어 둔다 —
   * 화면만 봐서는 "내가 올린 건 왜 기안함에도 진행함에도 있지" 를 알 수 없다.
   */
  '/approval': {
    title: '전자결재 — 화면 설명',
    body: (
      <>
        <HelpP>
          결재 문서가 <b>지금 어디까지 왔는지</b>에 따라 함이 나뉩니다. 같은 문서가 흐름을
          따라 함을 옮겨 다닙니다.
        </HelpP>
        <HelpList
          items={[
            { term: '기안하기', desc: '문서를 새로 씁니다. 상신하면 첫 결재자에게 넘어갑니다.' },
            { term: '결재대기', desc: '지금 내 차례인 문서. 앞사람이 결재해야 여기 들어옵니다.' },
            { term: '기안함', desc: '내가 올린 문서 전부. 지금 누구 자리에서 멈춰 있는지 봅니다.' },
            { term: '진행함', desc: '아직 끝나지 않은 문서. 결재선이 남아 있는 것들입니다.' },
            { term: '완료함 · 반려함', desc: '끝난 문서. 승인으로 끝났는지 반려로 끝났는지로 나뉩니다.' },
            { term: '참조함', desc: '내가 결재자는 아니지만 알고 있어야 하는 문서입니다.' },
          ]}
        />
        <HelpP>
          어느 함에 있는지 모르겠으면 <HelpPath>통합조회</HelpPath> 에서 기간·양식·기안자로
          찾으세요.
        </HelpP>
      </>
    ),
  },

  '/approval/progress': {
    title: '진행함 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>아직 끝나지 않은</b> 문서가 모입니다. 결재대기함과 다릅니다 — 결재대기는 내
          차례인 것만, 진행함은 내가 관련된 것 중 <b>아직 돌고 있는 것 전부</b>입니다.
        </HelpP>
        <HelpP>
          며칠째 멈춰 있는 문서가 보이면 결재선에서 누구 자리인지 확인하세요. 그 사람이
          자리를 비웠다면 <HelpPath>위임 설정</HelpPath> 으로 결재권을 넘겨야 흐릅니다.
        </HelpP>
      </>
    ),
  },

  '/approval/completed': {
    title: '완료함 — 화면 설명',
    body: (
      <>
        <HelpP>
          결재가 <b>끝까지 승인된</b> 문서입니다. 여기 들어온 문서는 더 고칠 수 없습니다 —
          고쳐야 한다면 새로 기안하세요.
        </HelpP>
        <HelpP>
          휴가 신청서는 완료되는 순간{' '}
          <HelpPath>인사관리 → 연차사용현황</HelpPath> 의 사용일수에 반영됩니다. 진행 중이거나
          반려된 것은 집계되지 않습니다.
        </HelpP>
      </>
    ),
  },

  '/approval/rejected': {
    title: '반려함 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>되돌아온</b> 문서입니다. 반려 사유를 먼저 읽으세요 — 결재자는 그 한 줄만 보고
          돌려보냅니다.
        </HelpP>
        <HelpP>
          반려된 문서는 고쳐서 다시 올리는 것이 아니라 <b>새로 기안</b>합니다. 지난 문서는
          그대로 기록으로 남습니다.
        </HelpP>
      </>
    ),
  },

  '/approval/reference': {
    title: '참조함 — 화면 설명',
    body: (
      <>
        <HelpP>
          내가 <b>결재자는 아니지만</b> 알고 있어야 하는 문서입니다. 기안자가 참조로 지정한
          것들입니다.
        </HelpP>
        <HelpP>
          여기 있는 문서는 <b>승인하거나 반려할 수 없습니다.</b> 읽기만 합니다 — 결재를
          해야 한다면 결재선에 들어가야 하므로 기안자에게 알려 주세요.
        </HelpP>
      </>
    ),
  },

  '/approval/draft': {
    title: '기안하기 — 화면 설명',
    body: (
      <>
        <HelpP>
          결재받을 문서를 씁니다. 양식을 고르면 그에 맞는 입력칸과 <b>결재선이 자동으로
          잡힙니다.</b>
        </HelpP>
        <HelpH>임시저장과 상신</HelpH>
        <HelpList
          items={[
            { term: '임시저장', desc: '나만 봅니다. 결재선에 올라가지 않습니다.' },
            { term: '상신', desc: '첫 결재자에게 넘어갑니다. 이때부터 기안함·진행함에 보입니다.' },
          ]}
        />
        <HelpH>잘못 올렸다면</HelpH>
        <HelpP>
          <b>회수</b>하면 됩니다. 아직 아무도 결재하지 않았을 때만 가능합니다. 한 명이라도
          승인했다면 반려를 요청하세요.
        </HelpP>
        <HelpH>휴가를 낼 때</HelpH>
        <HelpP>
          양식에서 <b>휴가 신청서</b>를 고르세요. 승인이 끝나면{' '}
          <HelpPath>인사관리 → 연차사용현황</HelpPath> 의 사용일수에 자동으로 반영됩니다.
          반려된 것은 집계되지 않습니다.
        </HelpP>
      </>
    ),
  },

  '/approval/inbox': {
    title: '결재대기 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>내 차례</b>인 문서만 모입니다. 앞 사람이 아직 결재하지 않은 문서는 여기에
          나타나지 않습니다.
        </HelpP>
        <HelpList
          items={[
            { term: '승인', desc: '다음 결재자에게 넘어갑니다. 마지막 사람이 승인하면 완료됩니다.' },
            { term: '반려', desc: '기안자에게 돌아갑니다. 사유를 적어 주세요 — 그것만 보고 고칩니다.' },
          ]}
        />
        <HelpP>
          자리를 비울 때는 <HelpPath>위임 설정</HelpPath> 에서 결재권을 넘겨 두세요. 그러지
          않으면 문서가 내 자리에서 멈춥니다.
        </HelpP>
      </>
    ),
  },

  '/approval/drafted': {
    title: '기안함 — 화면 설명',
    body: (
      <>
        <HelpP>
          내가 올린 문서를 봅니다. 지금 <b>누구 자리에서 멈춰 있는지</b> 결재선에서 확인할
          수 있습니다.
        </HelpP>
      </>
    ),
  },

  '/approval/forms': {
    title: '양식관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          결재 문서의 <b>틀</b>을 만듭니다. 양식마다 입력칸과 기본 결재선을 정해 두면,
          기안하는 사람은 고르기만 하면 됩니다.
        </HelpP>
        <HelpH>양식 코드는 바꾸지 마세요</HelpH>
        <HelpP>
          일부 코드는 시스템이 알아봅니다. 특히 <code>LEAVE</code> 는 휴가 신청서로 보아{' '}
          <HelpPath>연차사용현황</HelpPath> 집계에 씁니다. 코드를 바꾸면 그 연결이 끊깁니다.
        </HelpP>
        <HelpH>쓰지 않는 양식</HelpH>
        <HelpP>
          지우지 말고 <b>사용 안 함</b>으로 두세요. 그 양식으로 올린 과거 문서가 남아
          있습니다.
        </HelpP>
      </>
    ),
  },

  '/approval/rules': {
    title: '전결규정 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>금액에 따라 누가 마지막으로 결재하는지</b>를 정합니다. 예를 들어 100만원
          이하는 팀장까지, 그 위는 임원까지.
        </HelpP>
        <HelpP>
          구간이 겹치거나 비면 어느 규정이 적용될지 사람이 예측할 수 없습니다. 금액 구간을
          빈틈없이 이어 주세요.
        </HelpP>
      </>
    ),
  },

  '/approval/delegations': {
    title: '위임 설정 — 화면 설명',
    body: (
      <>
        <HelpP>
          휴가나 출장으로 자리를 비울 때 <b>내 결재권을 다른 사람에게</b> 넘깁니다.
        </HelpP>
        <HelpP>
          내가 위임한 것과 내가 위임받은 것만 보입니다. 남의 결재권을 임의로 가져올 수는
          없습니다.
        </HelpP>
      </>
    ),
  },

  '/approval/search': {
    title: '통합조회 — 화면 설명',
    body: (
      <>
        <HelpP>
          결재함을 가리지 않고 <b>조건으로 찾습니다</b> — 기간·양식·상태·기안자.
          지난 문서를 되짚을 때 씁니다.
        </HelpP>
      </>
    ),
  },

  /* ===================== 인사관리 ===================== */
  '/hr/employees': {
    title: '직원현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          우리 회사 구성원을 봅니다. <b>원본은 WORK&amp;Hub 입니다</b> — 입사·퇴사·부서 변경은
          Hub 의 구성원 화면에서 하고, 여기에는 그 결과가 따라옵니다.
        </HelpP>
        <HelpP>
          사람이 보이지 않는다면 Hub 에 아직 등록되지 않았거나 퇴사 처리된 것입니다.
        </HelpP>
      </>
    ),
  },

  '/hr/orgchart': {
    title: '조직도 — 화면 설명',
    body: (
      <>
        <HelpP>
          부서와 소속을 그림으로 봅니다. 부서는 <b>WORK&amp;Hub 의 조직</b>을 따라갑니다.
        </HelpP>
        <HelpP>자리가 비어 보이면 그 사람의 부서가 지정되지 않은 것입니다.</HelpP>
      </>
    ),
  },

  '/hr/leave-policy': {
    title: '연차정책 — 화면 설명',
    body: (
      <>
        <HelpP>
          근속연수에 따라 <b>며칠을 주는지</b>를 정합니다. 여기서 정한 규칙으로 전 직원의
          연차가 자동 계산됩니다.
        </HelpP>
        <HelpH>구간은 빈틈없이</HelpH>
        <HelpP>
          <code>1~2년 15일</code>, <code>3~4년 16일</code> 처럼 <b>끝 연수를 포함</b>해서
          적습니다. 1~2 다음은 3부터여야 이어집니다. 사이가 비면 그 연차의 직원이 하루도
          받지 못하므로 저장할 때 막아 줍니다.
        </HelpP>
        <HelpH>미리보기로 확인하세요</HelpH>
        <HelpP>
          입사일 몇 개를 넣어 보면 <b>실제로 며칠이 나오는지</b> 저장 전에 확인할 수
          있습니다.
        </HelpP>
        <HelpH>부여일수가 계산되지 않는다면</HelpH>
        <HelpP>
          그 사람의 <b>입사일</b>이 없는 것입니다. 입사일은 WORK&amp;Hub 의 구성원 정보에서
          넣습니다.
        </HelpP>
      </>
    ),
  },

  '/hr/leave-status': {
    title: '연차사용현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          사람별로 <b>부여·사용·잔여</b> 연차를 봅니다.
        </HelpP>
        <HelpList
          items={[
            { term: '부여', desc: '연차정책과 입사일로 계산한 올해 연차입니다.' },
            {
              term: '사용',
              desc: (
                <>
                  전자결재에서 <b>승인이 끝난 휴가 신청서</b>만 집계합니다. 올리기만 하고
                  결재가 안 끝난 것, 반려된 것은 들어가지 않습니다.
                </>
              ),
            },
            { term: '잔여', desc: '부여 − 사용.' },
          ]}
        />
        <HelpP>
          숫자가 비어 있으면 입사일이 없거나 연차정책이 활성화되지 않은 것입니다.
        </HelpP>
      </>
    ),
  },

  '/hr/work-participation': {
    title: '업무참여현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          사람별로 <b>지금 몇 건의 업무를 맡고 있는지</b> 봅니다. 일이 한쪽으로 몰렸는지
          확인할 때 씁니다.
        </HelpP>
        <HelpP>
          집계 대상은 <HelpPath>프로젝트 → 업무관리</HelpPath> 에 담당자로 지정된 업무입니다.
        </HelpP>
      </>
    ),
  },

  /* ===================== 자원 · 일정 ===================== */
  '/resource/assets': {
    title: '자원관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          회의실·차량처럼 <b>여럿이 나눠 쓰는 것</b>을 등록합니다. 여기 등록된 자원이 예약
          화면의 목록에 나타납니다.
        </HelpP>
        <HelpP>
          쓰지 않게 된 자원은 지우지 말고 <b>사용 안 함</b>으로 두세요 — 과거 예약 기록이
          남아 있습니다.
        </HelpP>
      </>
    ),
  },

  '/resource/reservations': {
    title: '자원 예약 — 화면 설명',
    body: (
      <>
        <HelpP>회의실·차량을 시간을 정해 잡습니다.</HelpP>
        <HelpP>
          <b>같은 자원의 같은 시간대는 겹쳐 잡을 수 없습니다.</b> 이미 잡혀 있으면 누가
          무엇으로 쓰고 있는지 알려 줍니다.
        </HelpP>
      </>
    ),
  },

  '/resource/usage': {
    title: '자원사용현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          자원이 <b>얼마나 쓰이는지</b> 봅니다. 늘 꽉 차 있다면 하나 더 둘 때가 된 것이고,
          비어 있다면 정리할 때입니다.
        </HelpP>
      </>
    ),
  },

  '/schedule/personal': {
    title: '개인일정 — 화면 설명',
    body: (
      <>
        <HelpP>내 일정입니다. 기본적으로 나만 봅니다.</HelpP>
        <HelpP>
          등록할 때 <b>부서 공유</b>나 <b>회사 공유</b>를 켜면 부서일정·회사일정에도
          나타납니다. 회의를 잡으면서 회의실까지 함께 예약할 수도 있습니다.
        </HelpP>
      </>
    ),
  },

  '/schedule/department': {
    title: '부서일정 — 화면 설명',
    body: (
      <>
        <HelpP>
          같은 부서 사람들이 <b>부서 공유로 올린</b> 일정이 모입니다. 내 부서와 그 아래
          부서까지 보입니다.
        </HelpP>
        <HelpP>부서가 지정되지 않은 사람은 부서일정으로 공유할 수 없습니다.</HelpP>
      </>
    ),
  },

  '/schedule/company': {
    title: '회사일정 — 화면 설명',
    body: (
      <>
        <HelpP>
          전사 행사처럼 <b>모두가 알아야 할 일정</b>입니다. 회사 공유로 올린 것만 보입니다.
        </HelpP>
      </>
    ),
  },

  /* ===================== 커뮤니티 ===================== */
  '/board/notice': {
    title: '공지사항 — 화면 설명',
    body: (
      <>
        <HelpP>전사에 알릴 내용을 올립니다. 파일을 함께 붙일 수 있습니다.</HelpP>
        <HelpP>
          글을 지우면 <b>붙어 있던 파일도 함께</b> 사라집니다. 되돌릴 수 없으니 확인하고
          지우세요.
        </HelpP>
      </>
    ),
  },

  '/board/archive': {
    title: '자료실 — 화면 설명',
    body: (
      <>
        <HelpP>
          두고두고 쓰는 문서를 모읍니다. 공지와 달리 <b>오래 남겨 두는</b> 자료가 들어갑니다.
        </HelpP>
      </>
    ),
  },

  '/board/education': {
    title: '교육자료 — 화면 설명',
    body: (
      <>
        <HelpP>신규 입사자 안내, 사용법 같은 교육 자료를 둡니다.</HelpP>
      </>
    ),
  },

  /* ===================== 설정 ===================== */
  '/settings/company': {
    title: '회사정보 — 화면 설명',
    body: (
      <>
        <HelpP>
          회사 정보는 <b>WORK&amp;Hub 에 저장됩니다.</b> 여기서 고치면 WORK&amp;Hub·WORK&amp;ECO
          에도 그대로 반영됩니다 — 제품마다 따로 적을 필요가 없습니다.
        </HelpP>
        <HelpH>AI 설정은 보기만 합니다</HelpH>
        <HelpP>
          <b>API Key 를 넣는 칸이 없어졌습니다.</b> 키는 서비스 공급사가 계약한 것을
          쓰고, 호출도 공급사 쪽에서 합니다 — 기업이 따로 준비할 것이 없습니다. 여기서는
          지금 쓸 수 있는지와 <b>보고서 1건에 얼마인지</b>만 보여 줍니다. 어느 엔진
          (Claude·Gemini)으로 돌릴지는 <HelpPath>WORK&amp;Hub → 기업 설정 → AI 보고서</HelpPath>
          에서 고릅니다.
        </HelpP>
        <HelpH>SMTP 칸도 없어졌습니다</HelpH>
        <HelpP>
          알림 메일은 공급사가 한 곳에서 보냅니다. 예전에는 기업이 여기에 자기 메일 서버
          계정을 넣어야 했고, 넣지 않은 기업은 <b>알림이 아예 가지 않았습니다.</b> 지금은
          따로 설정하지 않아도 나갑니다.
        </HelpP>
        <HelpH>누가 고칠 수 있나</HelpH>
        <HelpP>
          <b>기본값은 관리자만</b> 입니다. 다른 사람에게 맡기려면{' '}
          <HelpPath>설정 → 권한관리</HelpPath> 에서 그 부서·직급에 <b>회사정보</b> 메뉴의
          수정 권한을 열어 주세요. 열어 준 범위 그대로 저장 버튼이 동작합니다.
        </HelpP>
        <HelpH>로고와 2차 인증은 여기가 아닙니다</HelpH>
        <HelpP>
          기업 로고는 <HelpPath>WORK&amp;Hub → 기업 설정</HelpPath> 에서 올립니다. 한 번
          올리면 세 제품 상단에 함께 나옵니다. 2차 인증도 로그인을 맡는 Hub 의 보안 정책에서
          정합니다.
        </HelpP>
      </>
    ),
  },

  '/settings/menu-permissions': {
    title: '권한관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>누가 어느 메뉴를 쓸 수 있는지</b> 정합니다. 메뉴마다 조회·등록·수정·삭제·엑셀을
          따로 켜고 끕니다.
        </HelpP>
        <HelpH>먼저 「무엇을 고치나」를 고르세요</HelpH>
        <HelpList
          items={[
            {
              term: '직급 기준 (바탕)',
              desc: (
                <>
                  그 직급이 쓸 수 있는 <b>최대 범위</b>를 정합니다. 부서를 가리지 않고
                  <b> 그 직급 전원</b>에게 걸립니다. 회색 칸을 푸는 곳이 여기입니다.
                </>
              ),
            },
            {
              term: '조직 기준 (좁히기)',
              desc: (
                <>
                  위에서 연 것을 <b>특정 부서에서만 더 막습니다.</b> 넓힐 수는 없습니다 —
                  흐린 칸이 그것입니다.
                </>
              ),
            },
          ]}
        />
        <HelpP>
          예를 들어 <b>팀원에게 통합첨부파일 조회만 열어 주려면</b>, 「직급 기준」에서
          팀원을 고르고 통합첨부파일 행의 <b>조회(R)</b> 만 켜서 저장하면 됩니다.
          등록·수정·삭제는 끈 채로 두면 보기만 됩니다.
        </HelpP>
        <HelpH>역할이 기준, 부서는 그 안에서 좁히기</HelpH>
        <HelpList
          items={[
            {
              term: '역할별 권한',
              desc: '대표이사·부서장·팀장·팀원 단계로 정합니다. 이것이 그 사람이 쓸 수 있는 최대 범위입니다.',
            },
            {
              term: '조직별 권한',
              desc: (
                <>
                  부서에 따라 <b>더 좁힐 때</b> 씁니다. 역할이 닫아 둔 것을 부서에서 열 수는
                  없습니다 — 부서 규칙이 없으면 역할 그대로 적용됩니다.
                </>
              ),
            },
          ]}
        />
        <HelpH>새 회사에 기본으로 깔리는 권한</HelpH>
        <HelpP>
          대표이사·부서장은 설정을 뺀 전부, 팀장은 거기서 인사관리까지 빼고, 팀원은 영업활동
          관리·프로젝트 비용현황·통합첨부파일·진척현황을 더 뺍니다. 관리자는 모든 검사를
          지나갑니다.
        </HelpP>
        <HelpH>인사담당자</HelpH>
        <HelpP>
          <HelpPath>WORK&amp;Hub → 역할</HelpPath> 에서 <b>인사담당자</b>를 주면 인사관리
          메뉴가 통째로 열립니다. 여기서 하나씩 체크하지 않아도 됩니다.
        </HelpP>
        <HelpH>Hub 의 역할·권한과는 다릅니다</HelpH>
        <HelpP>
          <HelpPath>WORK&amp;Hub → 조직 → 역할·권한</HelpPath> 은 <b>플랫폼 권한</b>
          (조직 조회·파일·공지 등)을 다룹니다. <b>CRM 메뉴 권한은 이 화면에서만</b>
          정합니다. 다만 <b>인사담당자</b>는 예외라서, Hub 에서 그 역할을 주면 인사관리
          메뉴가 통째로 열립니다(반영까지 최대 1분).
        </HelpP>
        <HelpH>설정 메뉴는 기본이 닫혀 있습니다</HelpH>
        <HelpP>
          <b>공통코드관리·권한관리·회사정보·로그관리</b> 네 가지는 어느 직급에도 켜져 있지
          않습니다 — 관리자만 들어갑니다. 담당자에게 맡기려면 여기서 그 부서·직급에 해당
          메뉴를 열어 주세요.
        </HelpP>
        <HelpH>되돌리려면</HelpH>
        <HelpP>
          좁혀 둔 부서는 위에 <b>「역할 기본값으로」</b> 단추가 생깁니다. 누르면 이
          부서·직급에만 걸어 둔 설정이 지워지고 역할을 그대로 따릅니다. 체크를 전부
          켜서 저장하는 것과는 다릅니다 — 그렇게 하면 「역할이 나중에 바뀌어도 이 부서는
          이 값」 이라는 설정이 남습니다.
        </HelpP>
        <HelpP>
          바꾼 권한은 <b>30초 안에</b> 반영됩니다. 바로 보이지 않으면 잠시 뒤 새로고침하세요.
        </HelpP>
      </>
    ),
  },

  '/settings/codes': {
    title: '공통코드관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          화면의 <b>선택 목록</b>을 관리합니다 — 고객구분, 프로젝트구분, 휴가종류 같은 것들.
          여기에 넣은 값이 각 화면의 드롭다운에 나타납니다.
        </HelpP>
        <HelpP>
          왼쪽이 <b>대분류</b>, 오른쪽이 그 안에 들어가는 <b>상세 코드</b>입니다. 코드는
          대분류·상세 모두 <b>6자까지</b> 쓸 수 있고, 한 번 만든 코드 자체는 고칠 수
          없습니다 — 이미 그 코드로 저장된 자료가 있기 때문입니다. 코드명과 정렬 순서는
          언제든 고칠 수 있습니다.
        </HelpP>
        <HelpH>지우기 전에</HelpH>
        <HelpP>
          쓰고 있는 값을 지우면 그 값을 쓰던 기존 자료가 빈칸으로 보입니다. 더 쓰지 않을
          값이라면 <b>정렬 순서를 뒤로 미뤄</b> 목록 끝에 두는 편이 안전합니다.
          대분류는 상세 코드를 <b>모두 정리한 뒤에만</b> 지울 수 있습니다.
        </HelpP>
        <HelpH>고칠 수 없는 코드가 있습니다</HelpH>
        <HelpP>
          <b>로그 삭제 주기(LOGDEL·LOGDE1)</b> 는 서비스 공급사가 정합니다 — 수정도 삭제도
          막혀 있습니다. 법이 정한 최소 보관 기간이 있어서, 기업마다 다르게 두면 지켜야 할
          기간보다 먼저 지워지는 곳이 생기기 때문입니다. 목록에서 <b>공급사</b> 표가 붙은
          줄이 그것입니다.
        </HelpP>
      </>
    ),
  },

  '/settings/logs': {
    title: '로그관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          누가 언제 무엇을 했는지 남은 기록입니다. 자료가 사라졌거나 바뀌었을 때 되짚는
          자리입니다.
        </HelpP>
        <HelpP>
          기간은 <b>고른 날 하루 전체</b>를 봅니다 — 끝 날짜를 오늘로 두면 조금 전에 남은
          기록까지 함께 나옵니다. 대상 메뉴를 함께 고르면 그 화면에서 일어난 일만 추립니다.
        </HelpP>
        <HelpH>얼마나 보관하나</HelpH>
        <HelpP>
          보관 기간은 <b>서비스 공급사가 정하고</b>, 그보다 오래된 기록은 매일 자동으로
          지워집니다. 기업이 바꿀 수 없습니다 — 개인정보보호법이 접속기록을 최소 1년
          보관하도록 정하고 있기 때문입니다. 더 오래 남겨야 할 자료는 필요한 때{' '}
          <b>엑셀로 내려받아</b> 따로 보관하세요.
        </HelpP>
      </>
    ),
  },

  '/settings/users': {
    title: '직원정보관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          직원의 CRM 쪽 정보를 봅니다. <b>이름·부서·직위·입사일의 원본은 WORK&amp;Hub</b> 이고,
          여기에는 그 값이 따라옵니다.
        </HelpP>
        <HelpP>
          사람을 새로 넣거나 빼는 일은 WORK&amp;Hub 의 구성원 화면에서 합니다. 여기서 고칠 수
          있는 것은 내선번호처럼 CRM 안에서만 쓰는 값입니다.
        </HelpP>
      </>
    ),
  },

  '/settings/organizations': {
    title: '조직관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          부서를 봅니다. <b>원본은 WORK&amp;Hub 입니다</b> — 부서를 새로 만들거나 없애는 일은
          Hub 에서 하고, 여기서는 조회와 이름 수정만 합니다.
        </HelpP>
        <HelpP>여기서 이름을 고치면 Hub 에도 함께 반영됩니다.</HelpP>
      </>
    ),
  },
};

/**
 * 주소로 이 화면의 설명을 찾는다.
 *
 * 상세 화면처럼 뒤에 값이 붙는 주소(`/projects/list/12`)도 목록 화면의 설명을
 * 보여 준다 — 같은 업무의 연장이고, 상세마다 따로 쓸 만큼 다르지 않다.
 * 긴 주소부터 맞춰 보아 더 구체적인 설명이 있으면 그쪽이 이긴다.
 */
export function findScreenGuide(pathname) {
  if (!pathname) return null;
  if (SCREEN_GUIDE[pathname]) return SCREEN_GUIDE[pathname];

  const keys = Object.keys(SCREEN_GUIDE).sort((a, b) => b.length - a.length);
  const hit = keys.find((k) => pathname.startsWith(`${k}/`));
  return hit ? SCREEN_GUIDE[hit] : null;
}
